import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import './RemoveSpeaker.css';

function RemoveSpeaker() {
    const [speakers, setSpeakers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        fetchSpeakers();
    }, []);

    const fetchSpeakers = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/speakers`);
            setSpeakers(response.data || []);
            setError(null);
        } catch (error) {
            console.error('Error fetching speakers:', error);
            // If it's a 404 or the endpoint returns empty, treat it as no speakers instead of error
            if (error.response && (error.response.status === 404 || error.response.status === 204)) {
                setSpeakers([]);
                setError(null);
            } else {
                // Only show error for actual server errors (500, network issues, etc.)
                setError('Failed to fetch speakers. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    };

    const removeSpeaker = async (id, speakerName) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            html: `Do you want to remove <strong>"${speakerName}"</strong>?<br><span style="color: #dc2626; font-size: 0.9rem;">This action cannot be undone!</span>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#94a3b8',
            confirmButtonText: '<span style="display: flex; align-items: center; gap: 8px;"><svg style="width: 18px; height: 18px;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg> Yes, Remove It!</span>',
            cancelButtonText: 'Cancel',
            customClass: {
                popup: 'modern-swal-popup',
                confirmButton: 'modern-swal-confirm-danger',
                cancelButton: 'modern-swal-cancel'
            },
        });

        if (result.isConfirmed) {
            try {
                setDeletingId(id);
                await axios.delete(`${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/remove/speaker/${id}`);
                setSpeakers(speakers.filter(speaker => speaker._id !== id));
                Swal.fire({
                    icon: 'success',
                    title: 'Removed!',
                    text: 'The speaker has been removed successfully.',
                    confirmButtonColor: '#5e71f6',
                });
            } catch (error) {
                console.error('Error removing speaker:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to remove the speaker. Please try again.',
                    confirmButtonColor: '#5e71f6',
                });
            } finally {
                setDeletingId(null);
            }
        }
    };

    if (loading) {
        return (
            <div className="remove-speaker-wrapper">
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Loading speakers...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="remove-speaker-wrapper">
                <div className="error-container">
                    <div className="error-icon">!</div>
                    <p className="error-message">{error}</p>
                    <button className="retry-button" onClick={fetchSpeakers}>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="remove-speaker-wrapper">
            <div className="remove-speaker-container">
                {/* Header Section */}
                <div className="remove-speaker-header">
                    <h1 className="remove-speaker-title">Remove Speaker</h1>
                    <p className="remove-speaker-subtitle">Manage and remove speakers from the conference</p>
                </div>

                {/* Speaker List Card */}
                <div className="speaker-list-card">
                    <div className="speaker-list-header">
                        <h2 className="speaker-list-title">
                            <svg className="header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            Speaker List
                        </h2>
                        <span className="speaker-count-badge">
                            {speakers.length} {speakers.length === 1 ? 'Speaker' : 'Speakers'}
                        </span>
                    </div>

                    <div className="speaker-list-content">
                        {speakers.length === 0 ? (
                            <div className="no-speakers-message">
                                <svg className="no-speakers-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                </svg>
                                <p>No speakers found.</p>
                                <span className="no-speakers-subtext">All speakers have been removed or none have been added yet.</span>
                            </div>
                        ) : (
                            <div className="speakers-grid">
                                {speakers.map((speaker) => (
                                    <div className="speaker-card-item" key={speaker._id}>
                                        {/* Speaker Image */}
                                        {speaker.picture && (
                                            <div className="speaker-image-container">
                                                <img 
                                                    src={speaker.picture} 
                                                    alt={speaker.name}
                                                    className="speaker-image"
                                                />
                                            </div>
                                        )}

                                        {/* Speaker Content */}
                                        <div className="speaker-card-content">
                                            <div className="speaker-type-badge">
                                                <svg className="badge-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                                </svg>
                                                {speaker.type === 'KeynoteSpeaker' ? 'Keynote Speaker' : speaker.type === 'InvitedTalks' ? 'Invited Talk' : speaker.type}
                                            </div>

                                            <div className="speaker-info-section">
                                                <h3 className="speaker-name">
                                                    {speaker.title && <span className="speaker-title-prefix">{speaker.title}</span>}
                                                    {speaker.name}
                                                </h3>
                                                
                                                <div className="speaker-details-grid">
                                                    <div className="speaker-detail-item">
                                                        <svg className="detail-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                        </svg>
                                                        <span className="detail-text">{speaker.position}</span>
                                                    </div>

                                                    {speaker.university && (
                                                        <div className="speaker-detail-item">
                                                            <svg className="detail-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                            </svg>
                                                            <span className="detail-text">{speaker.university}</span>
                                                        </div>
                                                    )}

                                                    {speaker.website && (
                                                        <div className="speaker-detail-item">
                                                            <svg className="detail-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                                                            </svg>
                                                            <a href={speaker.website} target="_blank" rel="noopener noreferrer" className="website-link">
                                                                Website
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>

                                                {speaker.abstract && (
                                                    <div className="speaker-abstract">
                                                        <p className="abstract-text">{speaker.abstract}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Remove Button */}
                                        <button 
                                            className="remove-speaker-button" 
                                            onClick={() => removeSpeaker(speaker._id, speaker.name)}
                                            disabled={deletingId === speaker._id}
                                            title="Remove this speaker"
                                        >
                                            {deletingId === speaker._id ? (
                                                <>
                                                    <div className="button-spinner"></div>
                                                    Removing...
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="button-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                    Remove Speaker
                                                </>
                                            )}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RemoveSpeaker;
